import React, { useState, useEffect } from 'react';
import './OrganInput.css';
import axios from 'axios';
import { getOrgansByModality } from './organsData';

function OrganInput({ onSubmit, isLoading, disabled, modality = 'ct', apiBaseUrl = 'http://44.211.83.212:8080' }) {
  const [organName, setOrganName] = useState('');
  const [availableOrgans, setAvailableOrgans] = useState([]);
  const [categories, setCategories] = useState({});
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(['Special', 'Abdominal']);
  const [selectedOrgans, setSelectedOrgans] = useState([]);

  useEffect(() => {
    // Load static organ data (no API call needed)
    const staticData = getOrgansByModality(modality);
    setAvailableOrgans(staticData.organs);
    setCategories(staticData.categories);
    setCategoryOrder(staticData.category_order);
  }, [modality]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const MAX_ORGAN_SELECTION = 15; // Limit to avoid filesystem path length issues
  
  const toggleOrgan = (organName) => {
    setSelectedOrgans(prev => {
      if (prev.includes(organName)) {
        return prev.filter(o => o !== organName);
      } else {
        // Check if we've reached the limit
        if (prev.length >= MAX_ORGAN_SELECTION) {
          alert(`Maximum ${MAX_ORGAN_SELECTION} organs can be selected at once to avoid processing errors.`);
          return prev;
        }
        return [...prev, organName];
      }
    });
  };

  const toggleCategorySelect = (category) => {
    const categoryOrgans = categories[category].map(o => o.name);
    const allSelected = categoryOrgans.every(o => selectedOrgans.includes(o));
    
    if (allSelected) {
      setSelectedOrgans(prev => prev.filter(o => !categoryOrgans.includes(o)));
    } else {
      setSelectedOrgans(prev => {
        const newSelection = [...new Set([...prev, ...categoryOrgans])];
        if (newSelection.length > MAX_ORGAN_SELECTION) {
          alert(`Cannot select all organs in this category. Maximum ${MAX_ORGAN_SELECTION} organs allowed. Currently selected: ${prev.length}`);
          return prev;
        }
        return newSelection;
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedOrgans.length > 0 && !disabled) {
      onSubmit(selectedOrgans);
      setSelectedOrgans([]);
    } else if (organName.trim() && !disabled) {
      onSubmit(organName.trim());
      setOrganName('');
    } else {
    }
  };

  const handleOrganClick = (organ) => {
    setOrganName(organ.name);
    setShowSuggestions(false);
  };

  const filteredOrgans = availableOrgans.filter(organ =>
    organ.name.toLowerCase().includes(organName.toLowerCase()) ||
    (organ.display_name && organ.display_name.toLowerCase().includes(organName.toLowerCase()))
  );

  return (
    <div className="organ-input-container">
      <div className="card shadow-sm" style={{ maxWidth: '900px', margin: '0 auto', background: 'rgba(255, 255, 255, 0.95)' }}>
        <div className="card-body p-4">
          <form onSubmit={handleSubmit} className="organ-input-form">
            <div className="input-wrapper">
              <input
                type="text"
                value={organName}
                onChange={(e) => {
                  setOrganName(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                placeholder="Search organs (e.g., spleen, liver, heart)..."
                className="organ-input"
                disabled={disabled}
                autoComplete="off"
              />
              <button
                type="submit"
                className="submit-button"
                disabled={disabled || (selectedOrgans.length === 0 && !organName.trim())}
              >
                {isLoading ? 'Segmenting...' : selectedOrgans.length > 0 ? `Segment ${selectedOrgans.length} Organ${selectedOrgans.length > 1 ? 's' : ''}` : 'Segment'}
              </button>
            </div>

            {selectedOrgans.length > 0 && (
              <div className="selected-organs-display">
                <span className="selected-label">Selected ({selectedOrgans.length}):</span>
                <div className="selected-chips">
                  {selectedOrgans.map(organName => {
                    const organ = availableOrgans.find(o => o.name === organName);
                    return (
                      <span key={organName} className="selected-chip" style={{ borderColor: organ?.color }}>
                        {organ?.display_name || organName}
                        <button 
                          className="remove-chip" 
                          onClick={() => toggleOrgan(organName)}
                          disabled={disabled}
                          type="button"
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                  {selectedOrgans.length > 1 && (
                    <button 
                      className="clear-all-btn" 
                      onClick={() => setSelectedOrgans([])}
                      disabled={disabled}
                      type="button"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>
            )}

            {showSuggestions && organName && filteredOrgans.length > 0 && (
              <div className="suggestions-dropdown">
                {filteredOrgans.slice(0, 10).map((organ) => (
                  <div
                    key={organ.name}
                    className={`suggestion-item ${organ.name === 'all' ? 'all-organs-option' : ''}`}
                    onClick={() => handleOrganClick(organ)}
                  >
                    {organ.color && organ.name !== 'all' && (
                      <span className="organ-color-indicator" style={{ backgroundColor: organ.color }}></span>
                    )}
                    {organ.name === 'all' && <span className="all-icon">🏥</span>}
                    <div className="suggestion-content">
                      <strong>{organ.display_name || organ.name}</strong>
                      <span className="suggestion-meta">
                        {organ.category}
                        {organ.modality_display && (
                          <span className={`modality-badge ${organ.modality_display.toLowerCase().replace('+', '-')}`}>
                            {organ.modality_display}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </form>

          {Object.keys(categories).length > 0 && !organName && (
            <div className="quick-select">
              <p className="quick-select-title">Quick select by category:</p>
              <div className="organ-categories">
                {categoryOrder.map((category) => {
                  if (!categories[category] || categories[category].length === 0) return null;
                  const isExpanded = expandedCategories.includes(category);
                  
                  return (
                    <div key={category} className="organ-category">
                      <div className="category-header-row">
                        <button 
                          className={`category-header ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => toggleCategory(category)}
                          type="button"
                        >
                          <span className="category-icon">{isExpanded ? '▼' : '▶'}</span>
                          <span className="category-name">{category}</span>
                          <span className="category-count">({categories[category].length})</span>
                        </button>
                        {category !== 'Special' && (
                          <button
                            className="select-all-category-btn"
                            onClick={() => toggleCategorySelect(category)}
                            disabled={disabled}
                            title={categories[category].every(o => selectedOrgans.includes(o.name)) ? 'Deselect All' : 'Select All'}
                            type="button"
                          >
                            {categories[category].every(o => selectedOrgans.includes(o.name)) ? '☑' : '☐'} Select All
                          </button>
                        )}
                      </div>
                      
                      {isExpanded && (
                        <div className="organ-chips">
                          {category === 'Special' && (
                            <button
                              key="all"
                              className="organ-chip all-organs-chip"
                              onClick={() => setOrganName('all')}
                              disabled={disabled}
                              type="button"
                            >
                              🏥 All Organs
                            </button>
                          )}
                          {categories[category].map((organ) => (
                            <label
                              key={organ.name}
                              className={`organ-chip ${selectedOrgans.includes(organ.name) ? 'selected' : ''}`}
                              style={{ borderColor: organ.color }}
                              title={`${organ.description} (${organ.modality_display})`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedOrgans.includes(organ.name)}
                                onChange={() => toggleOrgan(organ.name)}
                                disabled={disabled}
                                className="organ-checkbox"
                              />
                              <span className="organ-chip-color" style={{ backgroundColor: organ.color }}></span>
                              <span className="organ-chip-name">{organ.display_name}</span>
                              <span className={`modality-badge ${organ.modality_display.toLowerCase().replace('+', '-')}`}>
                                {organ.modality_display}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrganInput;

